// ---------------------------------------------------------------------------
// «طلب من المنيو» on the till: a pending online order, loaded onto the
// Current Order for REVIEW, with the two things the shop may do with it.
//
// Nothing here creates anything. Opening the order on the till is reading
// it: the frozen lines go onto the cart as they were sent, at the prices the
// customer accepted, and the cart is locked. Confirm calls the SAME
// backend transition Menu Management calls - one service, two doors - and
// prints whatever plan it answers with, from the delivery order's own frozen
// figures. Reject calls the same rejection. Leaving does nothing at all: the
// order stays pending for whoever opens it next.
// ---------------------------------------------------------------------------

import { useState, type ReactNode } from 'react';
import { Bike, Globe, LogOut, ShoppingBag, X } from 'lucide-react';
import { useI18n } from '../../i18n/I18nProvider';
import { useConfirmOnlineOrder, useRejectOnlineOrder } from '../../api/menu.api';
import { ApiError } from '../../lib/apiClient';
import { toast } from '../../stores/toastStore';
import { Modal } from '../common/Modal';
import { formatLbp } from '../../utils/format';
import type { OnlineOrder } from '../../types';

/// The actions of a review, owned by the till page so the cart's own button
/// can be "confirm".
///
/// NOTHING PRINTS HERE. Confirming is not paying: the bench tickets are
/// written on the server against the order, and the paper - every ticket and
/// the receipt - comes out once, from the sale that pays the order in full.
/// The till's normal payment path owns that plan.
export function useMenuOrderReview({ order, onDone, onConfirmed }: { order: OnlineOrder | null; onDone: () => void; onConfirmed?: (id: string) => void }) {
  const { t } = useI18n();
  const confirm = useConfirmOnlineOrder();
  const reject = useRejectOnlineOrder();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');

  const fail = (caught: unknown) => toast.error(caught instanceof ApiError ? caught.message : t.errors.unexpectedBody);

  const confirmNow = async () => {
    if (!order || confirm.isPending) return;
    try {
      const result = await confirm.mutateAsync(order.id);
      if (result.alreadyConfirmed) {
        toast.info(t.menu.orders.alreadyConfirmed);
      } else {
        toast.success(t.menu.orders.confirmed, result.order.deliveryOrder?.orderNumber);
      }
      onDone();
      if (result.deliveryOrderId) onConfirmed?.(result.deliveryOrderId);
    } catch (caught) {
      fail(caught);
    }
  };

  const rejectNow = async () => {
    if (!order || reject.isPending) return;
    try {
      await reject.mutateAsync({ id: order.id, reason: reason.trim() || null });
      toast.success(t.menu.orders.rejected);
      setRejecting(false);
      onDone();
    } catch (caught) {
      fail(caught);
    }
  };

  const elements: ReactNode = (
    <>
      <Modal
        open={rejecting}
        onClose={() => setRejecting(false)}
        title={t.menu.orders.rejectTitle}
        size="sm"
        busy={reject.isPending}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setRejecting(false)} disabled={reject.isPending}>
              {t.common.cancel}
            </button>
            <button type="button" className="btn-danger" onClick={() => void rejectNow()} disabled={reject.isPending} data-testid="review-reject-confirm">
              {t.menu.orders.reject}
            </button>
          </>
        }
      >
        <label className="block">
          <span className="label">{t.menu.orders.rejectReason}</span>
          <select value={reason} onChange={(event) => setReason(event.target.value)} className="input" aria-label={t.menu.orders.rejectReason}>
            <option value="">—</option>
            {Object.values(t.menu.orders.reasons).map((text) => (
              <option key={text} value={text}>
                {text}
              </option>
            ))}
          </select>
        </label>
      </Modal>
    </>
  );

  return { confirmNow, openReject: () => setRejecting(true), busy: confirm.isPending || reject.isPending, elements };
}

/// The banner over the Current Order: whose order this is, and the way out.
export function MenuOrderReviewBanner({ order, busy, canConfirm, canReject, onConfirm, onReject, onExit }: { order: OnlineOrder; busy: boolean; canConfirm: boolean; canReject: boolean; onConfirm: () => void; onReject: () => void; onExit: () => void }) {
  const { t, locale } = useI18n();
  const address = [order.address.area, order.address.street, order.address.building, order.address.floor, order.address.landmark].filter(Boolean).join(' - ');
  const Type = order.orderType === 'DELIVERY' ? Bike : ShoppingBag;
  return (
    <div className="shrink-0 space-y-2 rounded-card border border-antique-gold/60 bg-antique-gold/10 p-3 text-sm" data-testid="menu-order-review" role="region" aria-label={t.menu.orders.reviewTitle}>
      <div className="flex items-start justify-between gap-2">
        <span className="flex items-center gap-2 font-semibold text-ink">
          <Globe className="h-4 w-4 text-butcher-green" aria-hidden="true" />
          {t.menu.orders.reviewTitle}
          <span className="numeric rounded-full bg-surface px-2 py-0.5 text-xs" dir="ltr">
            {order.orderNumber}
          </span>
        </span>
        <button type="button" onClick={onExit} className="flex h-8 w-8 items-center justify-center rounded-full text-ink-muted hover:bg-surface" aria-label={t.menu.orders.exitReview} disabled={busy} data-testid="review-exit">
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      <p className="text-ink">
        <span className="font-medium">{order.customerName}</span> · <span className="numeric" dir="ltr">{order.customerPhone}</span>
      </p>
      <p className="flex items-start gap-1.5 text-ink-muted">
        <Type className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>
          {order.orderType === 'DELIVERY' ? t.menu.orders.delivery : t.menu.orders.pickup}
          {order.orderType === 'DELIVERY' && address ? ` · ${address}` : ''}
          {order.orderType === 'DELIVERY' ? ` · ${order.deliveryFeePending ? t.menu.orders.feePending : `${t.menu.orders.deliveryFee}: ${formatLbp(order.deliveryFeeLbp, locale)}`}` : ''}
        </span>
      </p>
      {order.customerNotes ? <p className="rounded-control bg-surface px-2 py-1 text-xs text-ink-muted">{order.customerNotes}</p> : null}
      <p className="text-xs text-ink-muted">{t.menu.orders.reviewHint}</p>
      <div className="flex flex-wrap gap-2">
        {canConfirm ? (
          <button type="button" className="btn-primary flex-1" onClick={onConfirm} disabled={busy} data-testid="review-confirm">
            {t.menu.orders.confirm}
          </button>
        ) : null}
        {canReject ? (
          <button type="button" className="btn-danger" onClick={onReject} disabled={busy} data-testid="review-reject">
            {t.menu.orders.reject}
          </button>
        ) : null}
        <button type="button" className="btn-secondary" onClick={onExit} disabled={busy}>
          <LogOut className="h-4 w-4" aria-hidden="true" />
          {t.menu.orders.exitReview}
        </button>
      </div>
    </div>
  );
}
